import { generateGuid } from 'guid';
import { type AnyObject } from '@Types';
import { type ConversationContext } from 'TutorBotConversation';
import {
    type RealAiMessage,
    type PretendAiMessage,
    type ChatMessage,
    type HumanMessage,
    type TutorBotSystemMessage,
    type OutgoingMessageInputs,
    type TutorBotSystemMessageInputs,
    type HumanMessageInputs,
    type StructuredMessage,
    type AiMessageMetadata,
} from '../types/ChatMessage.types';

export function isHumanMessage(message: ChatMessage | null | undefined): message is HumanMessage {
    if (!message) return false;
    return (message as HumanMessage).role === 'human';
}

export function isTutorBotSystemMessage(message: ChatMessage | null | undefined): message is HumanMessage {
    if (!message) return false;
    return (message as TutorBotSystemMessage).role === 'tutor_bot_system';
}

export function isRealAiMessage(message: ChatMessage | null | undefined): message is RealAiMessage {
    if (!message) return false;
    const aiMessage = message as RealAiMessage;
    return (
        aiMessage.role === 'ai' &&
        (aiMessage as { contentType?: string }).contentType !== 'structured_message' &&
        'aiMessageMetadata' in aiMessage &&
        aiMessage.isPretend === false
    );
}

export function isPretendAiMessage(message: ChatMessage | null | undefined): message is PretendAiMessage {
    if (!message) return false;
    const pretendAiMessage = message as PretendAiMessage;
    return pretendAiMessage.role === 'ai' && pretendAiMessage.isPretend;
}

export function isAiMessage(message: ChatMessage | null | undefined): message is RealAiMessage | PretendAiMessage {
    if (!message) return false;
    return isRealAiMessage(message) || isPretendAiMessage(message);
}

export function isStructuredMessage(message: ChatMessage | null | undefined): message is StructuredMessage {
    if (!message) return false;
    const structuredMessage = message as StructuredMessage;
    return structuredMessage.contentType === 'structured_message';
}

export function isOutgoingMessage(
    message: ChatMessage | null | undefined,
): message is HumanMessage | TutorBotSystemMessage {
    return isHumanMessage(message) || isTutorBotSystemMessage(message);
}

export function getMessageSources(message: ChatMessage): RealAiMessage['sources'] {
    return isRealAiMessage(message) ? message.sources : ([] as RealAiMessage['sources']);
}

export function createRealAiMessage({
    conversationId,
    contentType,
}: {
    conversationId: RealAiMessage['conversationId'];
    contentType: RealAiMessage['contentType'];
}): RealAiMessage {
    /* Add the skeleton AI response message to the store, which
               we will update when the response data streams in */
    return {
        ...createBaseMessageAttrs(),
        role: 'ai',
        content: '',
        contentType,
        conversationId,
        hasError: false,
        complete: false,
        sources: [],
        footnoteReferences: {},
        isPretend: false,
        status: null,
        payload: null,
        aiMessageMetadata: {
            botVersion: '0.0.0',
            algorithm: 'bot_page',
            totalTokens: 0,
            totalTokensFromCache: 0,
            totalCost: 0.0,
            totalCostFromCache: 0.0,
            promptTokens: 0,
            promptTokensFromCache: 0,
            completionTokens: 0,
            completionTokensFromCache: 0,
        },
        conversationContext: {},
        canceled: null,
        isInitialMessage: false,
    };
}

export function createPretendAiMessage({
    content,
    id,
    conversationId,
    isInitialMessage = false,
}: {
    content: PretendAiMessage['content'];
    id?: PretendAiMessage['id'];
    conversationId: PretendAiMessage['conversationId'];
    isInitialMessage?: boolean;
}): PretendAiMessage {
    id = id ?? generateGuid();
    return {
        ...createBaseMessageAttrs(),
        role: 'ai',
        content,

        // it's hard to imagine a situation where a pretend AI message would not be text
        contentType: 'text',
        id,
        isPretend: true,
        complete: true,
        conversationId,
        canceled: null,
        isInitialMessage,
    };
}

export function createStructuredMessage({
    conversationId,
    payload,
    id,
    aiMessageMetadata,
    conversationContext = {},
    sourceMessageId = null,
}: {
    conversationId: StructuredMessage['conversationId'];
    payload: AnyObject;
    id?: StructuredMessage['id'];
    aiMessageMetadata?: AiMessageMetadata | null;
    conversationContext?: ConversationContext;
    sourceMessageId?: string | null;
}): StructuredMessage {
    const baseAttrs = createBaseMessageAttrs();
    return {
        ...baseAttrs,
        id: id ?? baseAttrs.id,
        role: 'ai',
        content: typeof payload?.type === 'string' ? (payload.type as string) : '',
        contentType: 'structured_message',
        conversationId,
        payload,
        complete: true,
        hasError: false,
        canceled: null,
        hidden: true,
        isInitialMessage: false,
        aiMessageMetadata: aiMessageMetadata ?? null,
        conversationContext,
        sourceMessageId,
    };
}

export function createHumanMessage({
    content,
    contentType,
    conversationId,
    isRetryOfMessageId = null,
}: {
    content: HumanMessage['content'];
    contentType: HumanMessage['contentType'];
    conversationId: HumanMessage['conversationId'];
    isRetryOfMessageId?: HumanMessage['isRetryOfMessageId'];
}): HumanMessage {
    return {
        ...createBaseMessageAttrs(),
        role: 'human',
        content,
        contentType,
        conversationId,
        complete: true,
        isRetryOfMessageId,
        payload: null,
        canceled: null,
        isInitialMessage: false,
    };
}

export function createBaseMessageAttrs() {
    return {
        id: generateGuid(),
        createdAt: new Date().getTime(),
        hasError: false as const,
        payload: null,
        hidden: false as const,
    };
}

export function isTutorBotMessageInputs(inputs: OutgoingMessageInputs): inputs is TutorBotSystemMessageInputs {
    return inputs.role === 'tutor_bot_system';
}

export function isHumanMessageInputs(inputs: OutgoingMessageInputs): inputs is HumanMessageInputs {
    return inputs.role === 'human';
}
